import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArchiveContext } from "../App";
import { SecurityEvent } from "../types";
import SEOMeta, { generateEventSEO } from "../components/SEOMeta";

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { getEventById } = useContext(ArchiveContext);
  const [event, setEvent] = useState<SecurityEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const adContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (eventId) {
        const data = await getEventById(eventId);
        setEvent(data);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [eventId, getEventById]);

  // Load Google AdSense ad when component mounts and when event data changes

useEffect(() => { if (event && adContainerRef.current && typeof window !== 'undefined') { 
  // Clear previous ad content
  adContainerRef.current.innerHTML = ''; 
  // Create ins element for the ad 
  const ins = document.createElement('ins'); 
  ins.className = 'adsbygoogle'; 
  ins.style.display = 'block'; 
  ins.style.textAlign = 'center'; 
  ins.style.minWidth = '300px';
  ins.style.minHeight = '250px';
  ins.setAttribute('data-ad-client', event.adClient || 'ca-pub-9325373695328890'); 
  ins.setAttribute('data-ad-slot', event.adSlot || '3721511676'); 
  ins.setAttribute('data-ad-layout', 'in-article') 
    ins.setAttribute('data-ad-format', 'fluid'); 
  // Add the ad element to the container 
  adContainerRef.current.appendChild(ins); 
  // Trigger ad load 
  setTimeout(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error("Adsense error:", e);
      }
    }, 500); // slight delay allows layout to settle
  }
}, [event]);

  // ========== SAFE ACCESSOR FUNCTIONS ==========
  const safeGet = <T,>(value: T | undefined | null, fallback: T): T => {
    return value !== undefined && value !== null ? value : fallback;
  };

  const getVictimName = () =>
    safeGet(event?.victim?.name, "Target Organization");
  const getVictimSector = () => safeGet(event?.victim?.node, "Various Sectors");
  const getVictimCountry = () => safeGet(event?.victim?.country, "Global");
  const getVictimCity = () => safeGet(event?.victim?.city, "Not Specified");

  const getAttackerName = () =>
    safeGet(event?.attacker?.name, "Unknown Threat Actor");
  const getAttackerOrg = () =>
    safeGet(event?.attacker?.organization, "Freelance / State-Sponsor");
  const getAttackerCountry = () => safeGet(event?.attacker?.country, "Unknown");
  const getAttackerCity = () => safeGet(event?.attacker?.city, "Not Specified");

  const getAttackType = () => safeGet(event?.attackType, "Cyber Attack");
  const getTargetNode = () => safeGet(event?.targetNode, "Various Systems");
  const getTargetedAsset = () => safeGet(event?.targetedAsset, "Not Specified");
  const getStrategicImpact = () =>
    safeGet(event?.strategicImpact, "Classified");
  const getOperationalLosses = () =>
    safeGet(event?.operationalLosses, "Impact assessment classified");

  const getTrafficFlowAnalysis = () =>
    safeGet(event?.trafficFlowAnalysis, "Traffic analysis data not available");
  const getBarrierBypass = () =>
    safeGet(event?.barrierBypass, "Security bypass method not documented");
  const getExecutionOnTarget = () =>
    safeGet(event?.executionOnTarget, "Execution details classified");

  const getDefenseStack = () =>
    safeGet(event?.preAttackPosture?.defenseStack, []);
  const getCriticalLimitations = () =>
    safeGet(
      event?.preAttackPosture?.criticalLimitations,
      "Defense limitations not documented"
    );

  const getRecoveryStack = () =>
    safeGet(event?.postAttackPosture?.recoveryStack, []);
  const getContainmentLimitations = () =>
    safeGet(
      event?.postAttackPosture?.containmentLimitations,
      "Containment challenges not documented"
    );

  const getIncidentResponse = () =>
    safeGet(
      event?.strategicEvolution?.incidentResponse,
      "Incident response details not available"
    );
  const getModernizedDefense = () =>
    safeGet(
      event?.strategicEvolution?.modernizedDefenseStrategy,
      "Modern defense strategies not documented"
    );

  // ========== RENDER HELPER ==========
  const renderOptionalField = (value: any, fallback = "Not Available") => {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    return value;
  };

  const renderOptionalArray = (
    array: any[] | undefined,
    fallbackMessage = "No data available"
  ) => {
    if (!array || array.length === 0) {
      return (
        <p className="text-[13px] text-slate-500 italic">{fallbackMessage}</p>
      );
    }
    return null; // Let parent component handle rendering
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-4">
        <div className="w-8 h-8 border-2 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-[10px] font-black font-mono uppercase tracking-[0.4em] text-slate-500 italic">
          Accessing Ledger Node...
        </p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-black uppercase tracking-tighter text-white">
          Record Not Found
        </h1>
        <Link
          to="/decades"
          className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-[0.2em]"
        >
          Return to Index
        </Link>
      </div>
    );
  }

  // Generate SEO data with safe access
  const seoData = generateEventSEO(event);

  // UI Components
  const Label = ({ children }: { children?: React.ReactNode }) => (
    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
      {children}
    </p>
  );

  const Value = ({
    children,
    color = "text-white",
    fallback = "Not Available",
  }: {
    children?: React.ReactNode;
    color?: string;
    fallback?: string;
  }) => (
    <p className={`text-[15px] font-semibold ${color}`}>
      {children !== undefined && children !== null && children !== ""
        ? children
        : fallback}
    </p>
  );

  const SectionHeader = ({ title, icon }: { title: string; icon?: string }) => (
    <h2 className="text-[13px] font-bold text-blue-400 border-b border-blue-500/20 pb-2 mb-4 flex items-center gap-2">
      {icon && <i className={`fa-solid ${icon} text-[11px]`}></i>}
      {title}
    </h2>
  );

  // Check for optional features
  const hasAds = !!(event.adSlot && event.adClient);
  const hasAffiliate = !!(
    event.affiliateInfo &&
    (event.affiliateInfo.productName || event.affiliateInfo.link)
  );
  const hasReferences = !!(event.references && event.references.length > 0);

  const displayYear =
    event.eventYear < 0 ? `${Math.abs(event.eventYear)}` : event.eventYear;

  const canonicalUrl = `https://cyberannals.com/event/${event.id}`;

  // ========== SEO KEYWORD MAPPING ==========
  const getEventKeywords = (): string => {
    if (!eventId) return "cybersecurity, cyber attack, data breach";

    // Define the keyword map with proper typing
    const keywordMap: Record<string, string> = {
      // ===== Major NPM Supply Chain Attacks (2025) =====
      "npm-supply-chain-chalk-debug":
        "chalk npm compromise, debug npm attack, september 2025 npm breach, npm package hijacking, credential theft, open source supply chain",
      "shai-hulud-v1-npm-worm":
        "shai hulud npm worm, september 2025 npm attack, self-replicating malware, npm supply chain worm, postinstall attack, credential harvesting, cisa alert npm",
      "shai-hulud-v2-npm-worm":
        "shai hulud 2.0, november 2025 npm attack, the second coming npm worm, bun runtime malware, preinstall attack, dead man switch, destructive payload",
      "shai-hulud-v3-npm-worm":
        "shai hulud 3.0, december 2025 npm variant, modified npm worm, supply chain attack evolution, npm security",

      // ===== Historic & Destructive Worms =====
      stuxnet:
        "stuxnet, stuxnet worm, cyber weapon, industrial control system attack, iran nuclear facility, zero-day exploit, cyber warfare",
      "morris-worm":
        "morris worm, first computer worm, 1988 internet worm, robert morris, history of malware",
      notpetya:
        "notpetya, petya ransomware, notpetya attack 2017, ukraine cyber attack, maersk notpetya, eternalblue, destructive malware",
      wannacry:
        "wannacry, wannacry ransomware, wannacry attack 2017, nhs ransomware, eternalblue exploit, worldwide ransomware attack",
      conficker:
        "conficker worm, conficker virus, downadup, windows worm, botnet",
      mydoom:
        "mydoom, mydoom virus, email worm, fastest spreading worm, novarg",
      "sql-slammer":
        "sql slammer, slammer worm, 2003 sql server worm, sapphire worm, denial of service",
      iloveyou:
        "iloveyou virus, love bug virus, love letter worm, vbs malware, 2000 computer virus",
      "nimda-2001":
        "nimda worm, nimda virus, iis worm, 2001 malware, code red nimda",
      "sasser-2004":
        "sasser worm, sasser virus, lsass exploit, 2004 windows worm",
      "anna-kournikova-2001":
        "anna kournikova virus, vbs worm, email hoax virus, 2001 malware",
      "blaster-2003":
        "blaster worm, msblast, lovsan, 2003 windows worm, rpc dcom exploit",

      // ===== Major Data Breaches =====
      "equifax-data-breach":
        "equifax data breach, equifax hack 2017, credit report theft, social security number leak, data breach settlement",
      "yahoo-data-breach":
        "yahoo data breach, yahoo hack, 3 billion accounts breached, largest data breach",
      "target-data-breach-2013":
        "target data breach 2013, target hack, credit card breach, retail data breach",
      "marriott-international":
        "marriott data breach, marriott hack 2018, starwood breach, passport number theft",
      "capital-one-data-breach":
        "capital one data breach 2019, capital one hack, aws s3 breach, paige thompson",
      opm: "opm data breach, office of personnel management hack, us government breach, security clearance theft",
      "home-depot-breach":
        "home depot data breach 2014, home depot hack, point of sale malware",
      "jpmorgan-chase-breach":
        "jpmorgan chase data breach 2014, jpmorgan hack, financial institution breach",
      "ashley-madison-breach":
        "ashley madison breach, ashley madison hack 2015, infidelity site data leak",
      "anthem-breach":
        "anthem data breach, anthem hack 2015, health insurance data theft",
      "sony-pictures-hack":
        "sony pictures hack 2014, sony attack, north korea cyber attack, the interview film",

      // ===== State-Sponsored & Espionage Attacks =====
      "operation-aurora":
        "operation aurora, aurora google hack, china cyber espionage 2009, zero-day attack",
      "flame-malware":
        "flame malware, flame virus, skywiper, middle east cyber espionage",
      "shamoon-2012":
        "shamoon, shamoon virus, saudi aramco attack, disttrack wiper malware",
      "darkseoul-2013":
        "dark seoul cyber attack, south korea cyber attack, 2013 banking breach",
      "ukraine-cyber-attack-2015":
        "ukraine power grid attack 2015, blackenergy malware, cyber attack on critical infrastructure",
      "estonia-cyber-attacks-2007":
        "estonia cyber attack 2007, bronze soldier riots, ddos attack on nation",
      "ghostnet-2009":
        "ghostnet cyber espionage, china hacking network, tibetan targets",

      // ===== Ransomware & Extortion Attacks =====
      "colonial-pipeline-ransomware":
        "colonial pipeline ransomware, colonial pipeline hack 2021, dark side ransomware, critical infrastructure attack",
      "bad-rabbit-2017":
        "bad rabbit ransomware, bad rabbit 2017, disk encryptor malware, notpetya variant",
      "kaseya-vsa-2021":
        "kaseya vsa ransomware attack, revil ransomware, supply chain ransomware 2021",
      "jbs-foods-2021":
        "jbs foods ransomware attack, meat processor hack, revil ransomware jbs",

      // ===== Software Supply Chain & Zero-Day Attacks =====
      "microsoft-solarwinds-sunburst":
        "solarwinds hack, sunburst malware, solarwinds supply chain attack, nation state backdoor",
      "log4j-log4shell-2021":
        "log4shell, log4j vulnerability, log4j exploit, cve-2021-44228, remote code execution",
      "microsoft-hafnium-2021":
        "microsoft exchange hafnium, proxy logon vulnerabilities, china state-sponsored attack",
      "microsoft-sharepoint-toolshell-2025":
        "microsoft sharepoint toolshell, 2025 sharepoint vulnerability, server-side request forgery",
      "sony-bmg-rootkit-2005":
        "sony bmg rootkit, sony drm rootkit scandal, 2005 copy protection malware",

      // ===== Critical Infrastructure & DDoS Attacks =====
      "dyn-ddos-attack":
        "dyn ddos attack 2016, mirai botnet dyn, internet outage ddos",
      "mirai-botnet":
        "mirai botnet, iot botnet, internet of things malware, source code release",
      "viasat-ka-sat-2022":
        "viasat ka-sat attack, satellite internet hack, ukraine cyber warfare 2022",
      "bank-heist":
        "bangladesh bank heist, swift banking hack, federal reserve bank cyber theft",
      "talktalk-data-breach":
        "talktalk data breach 2015, uk telecom hack, customer data theft",

      // ===== Social Engineering & Phishing Campaigns =====
      "facebook-cambridge-analytica":
        "cambridge analytica scandal, facebook data scandal, user data harvesting",
      "twitter-bitcoin-scam":
        "twitter bitcoin scam 2020, celebrity account hack, social media cryptocurrency scam",
      "rsa-securid":
        "rsa securid hack, rsa security breach 2011, two-factor authentication compromise",
      "night-dragon":
        "operation night dragon, energy sector cyber espionage, china hacking campaign",

      // ===== Other Notable Incidents =====
      "moveit-supply-chain-attack":
        "moveit supply chain attack, clop ransomware moveit, progress software vulnerability",
      "national-public-data-npd-2024":
        "national public data npd breach, data broker leak, 2024 data exposure",
      "vpnfilter-2018":
        "vpnfilter malware, router firmware malware, russia state-sponsored malware",
      "tjx-companies-2007":
        "tjx companies data breach, tj maxx hack, credit card data theft 2007",
      "sony-playstation-network":
        "sony playstation network hack, psn outage 2011, gaming network data breach",

      // ====  Distributed Denial of Service (DDoS) attacks ===
      "cyberattacks on Estonia 2007":
        "cyberattacks on estonia 2007, 2007 cyberattacks on estonia,2007 cyberattacks against estonia, 2007 cyberattacks, estonian cyber attacks 2007, estonia 2007 cyber attack, cyber attacks on estonia 2007, cyber attack estonia, cyber attack on estonia 2007, ddos attack estonia, cyber warefare estonia, cyber crime estonia, estonia cyber attack case study, who was responsible for the 2007 estonia cyber attack, estonia cyber attack summary, analysis of the cyber attacks against estonia, hybrid threats 2007 cyber attacks on estonia, what was the reason for the cyberattacks made against estonia in 2007",
    };

    // Use bracket notation with eventId as string - TypeScript will allow this now
    return keywordMap[eventId] || "cybersecurity, cyber attack, data breach";
  };

  return (
    <>
      <SEOMeta
        title={seoData.title}
        description={seoData.description}
        keywords={getEventKeywords()}
        canonical={canonicalUrl}
        ogType={seoData.ogType}
        articlePublishedTime={seoData.articlePublishedTime}
        articleModifiedTime={seoData.articleModifiedTime}
        articleSection={seoData.articleSection}
        structuredData={seoData.structuredData}
      />

      <div className="max-w-5xl mx-auto pb-20 space-y-16 animate-in px-4 md:px-6">
        {/* HEADER SECTION */}
        <header className="pt-8 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <nav className="flex items-center gap-2 text-[12px] font-medium text-slate-400">
              <Link
                to="/decades"
                className="hover:text-white transition-colors"
              >
                Eras
              </Link>
              <i className="fa-solid fa-slash text-[10px] opacity-20"></i>
              <Link
                to={`/year/${event.eventYear}`}
                className="hover:text-white transition-colors"
              >
                Year: {displayYear}
              </Link>
              <i className="fa-solid fa-slash text-[10px] opacity-20"></i>
              <span className="text-blue-400">Dossier</span>
            </nav>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[12px] font-medium text-slate-400 hover:text-white transition-colors group"
            >
              <i className="fa-solid fa-arrow-left transition-transform group-hover:-translate-x-1"></i>
              Return to Ledger
            </button>
          </div>

          <div className="space-y-3">
            <Label>Historical Node Reference</Label>
            {/* MAIN VISIBLE H1 TAG - ADDED FALLBACK */}
            <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
              {event.title || "Security Event Analysis"}
            </h1>
          </div>

          {/* QUICK STATS GRID */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-white/10">
            <div>
              <Label>Chronicle Year</Label>
              <Value>{displayYear}</Value>
            </div>
            <div>
              <Label>Attack Vector</Label>
              <Value>{getAttackType()}</Value>
            </div>
            <div>
              <Label>Compromised Node</Label>
              <Value>{getTargetNode()}</Value>
            </div>
            <div>
              <Label>Targeted Asset</Label>
              <Value>{getTargetedAsset()}</Value>
            </div>
          </div>
        </header>

        {/* THREAT ACTOR & VICTIM PROFILES */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div
            className={`${
              hasAds ? "lg:col-span-8" : "lg:col-span-12"
            } grid grid-cols-1 md:grid-cols-2 gap-5`}
          >
            {/* ATTACKER PROFILE */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-5">
              <SectionHeader
                title="Threat Actor Analysis"
                icon="fa-user-secret"
              />
              <div className="space-y-3">
                <div>
                  <Label>Assigned Alias / Group</Label>
                  <Value>{getAttackerName()}</Value>
                </div>
                <div>
                  <Label>Organization / Unit</Label>
                  <Value>{getAttackerOrg()}</Value>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label>Inferred City</Label>
                    <Value>{getAttackerCity()}</Value>
                  </div>
                  <div>
                    <Label>Source Country</Label>
                    <Value>{getAttackerCountry()}</Value>
                  </div>
                </div>
              </div>
            </div>

            {/* VICTIM PROFILE */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-5">
              <SectionHeader
                title="Victim Node Profile"
                icon="fa-building-shield"
              />
              <div className="space-y-3">
                <div>
                  <Label>Primary Target Name</Label>
                  <Value>{getVictimName()}</Value>
                </div>
                <div>
                  <Label>Industry Sector</Label>
                  <Value>{getVictimSector()}</Value>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label>Node City</Label>
                    <Value>{getVictimCity()}</Value>
                  </div>
                  <div>
                    <Label>Node Country</Label>
                    <Value>{getVictimCountry()}</Value>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AD SLOT (Only shown if ad data exists) */}
          {hasAds && (
            <div className="lg:col-span-4 h-full min-h-[250px] min-w-[250px] w-full bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden"
                  id="event-detail-ad"
                  ref={adContainerRef}
                >
            </div>
          )}
        </section>
        {/* ATTACKER & VICTIM TOOLS SECTION */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* ATTACKER ARSENAL */}
          <div className="space-y-5">
            <SectionHeader
              title="Attacker Weaponization Ledger"
              icon="fa-microchip"
            />
            <div className="space-y-3">
              {renderOptionalArray(
                event.attackerArsenal,
                "No weaponization data available"
              )}
              {event.attackerArsenal &&
                event.attackerArsenal.length > 0 &&
                event.attackerArsenal.map((tool, i) => (
                  <div
                    key={i}
                    className="p-4 bg-white/5 border-l-2 border-red-500/50 rounded-r-lg hover:bg-white/10 transition-all"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <Label>
                        {renderOptionalField(tool.name, "Unknown Tool")}
                      </Label>
                      <span className="text-[10px] text-slate-500">
                        Bldr: {renderOptionalField(tool.builder, "Unknown")}
                      </span>
                    </div>
                    <p className="text-[13px] text-slate-300 leading-relaxed">
                      {renderOptionalField(
                        tool.mechanism,
                        "Mechanism not documented"
                      )}
                    </p>
                  </div>
                ))}
            </div>
          </div>

          {/* VICTIM SYSTEMS */}
          <div className="space-y-5">
            <SectionHeader
              title="Victim Systems Compromised"
              icon="fa-network-wired"
            />
            <div className="space-y-3">
              {renderOptionalArray(
                event.victimSystemsAffected,
                "No system compromise data available"
              )}
              {event.victimSystemsAffected &&
                event.victimSystemsAffected.length > 0 &&
                event.victimSystemsAffected.map((tool, i) => (
                  <div
                    key={i}
                    className="p-4 bg-white/5 border-l-2 border-blue-500/50 rounded-r-lg hover:bg-white/10 transition-all"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <Label>
                        {renderOptionalField(tool.name, "Unknown System")}
                      </Label>
                      <span className="text-[10px] text-slate-500">
                        Bldr:{" "}
                        {renderOptionalField(tool.builder, "Various Vendors")}
                      </span>
                    </div>
                    <p className="text-[13px] text-slate-300 leading-relaxed">
                      {renderOptionalField(
                        tool.mechanism,
                        "Compromise method not documented"
                      )}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* TECHNICAL ANALYSIS SECTION */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* TRAFFIC FLOW & BARRIER BYPASS */}
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <SectionHeader
                title="Traffic Flow Analysis"
                icon="fa-chart-network"
              />
              <p className="text-[15px] text-slate-300 leading-relaxed">
                {getTrafficFlowAnalysis()}
              </p>
            </div>

            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <SectionHeader
                title="Barrier Bypass Method"
                icon="fa-shield-virus"
              />
              <p className="text-[15px] text-slate-300 leading-relaxed">
                {getBarrierBypass()}
              </p>
            </div>
          </div>

          {/* EXECUTION & IMPACT */}
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <SectionHeader title="Execution on Target" icon="fa-crosshairs" />
              <p className="text-[15px] text-slate-300 leading-relaxed">
                {getExecutionOnTarget()}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <SectionHeader title="Strategic Impact" icon="fa-chess-board" />
                <p className="text-[15px] text-slate-300 leading-relaxed">
                  {getStrategicImpact()}
                </p>
              </div>

              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <SectionHeader
                  title="Operational Losses"
                  icon="fa-sack-dollar"
                />
                <p className="text-[15px] text-slate-300 leading-relaxed">
                  {getOperationalLosses()}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* DEFENSE & RECOVERY SECTION */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* PRE-ATTACK POSTURE */}
          <div className="space-y-5">
            <SectionHeader
              title="Pre-Attack Defense Posture"
              icon="fa-shield"
            />
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4">
              <div>
                <Label>Defense Stack</Label>
                {renderOptionalArray(
                  getDefenseStack(),
                  "No pre-attack defense data available"
                )}
                {getDefenseStack().length > 0 &&
                  getDefenseStack().map((tool, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <p className="text-[14px] text-white font-medium">
                        {renderOptionalField(tool.name)}
                      </p>
                      <p className="text-[12px] text-slate-400">
                        {renderOptionalField(tool.builder)}
                      </p>
                    </div>
                  ))}
              </div>
              <div>
                <Label>Critical Limitations</Label>
                <p className="text-[15px] text-slate-300 leading-relaxed">
                  {getCriticalLimitations()}
                </p>
              </div>
            </div>
          </div>

          {/* POST-ATTACK POSTURE */}
          <div className="space-y-5">
            <SectionHeader
              title="Post-Attack Recovery Posture"
              icon="fa-heart-pulse"
            />
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4">
              <div>
                <Label>Recovery Stack</Label>
                {renderOptionalArray(
                  getRecoveryStack(),
                  "No recovery data available"
                )}
                {getRecoveryStack().length > 0 &&
                  getRecoveryStack().map((tool, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <p className="text-[14px] text-white font-medium">
                        {renderOptionalField(tool.name)}
                      </p>
                      <p className="text-[12px] text-slate-400">
                        {renderOptionalField(
                          tool.mechanism,
                          "Recovery mechanism"
                        )}
                      </p>
                    </div>
                  ))}
              </div>
              <div>
                <Label>Containment Limitations</Label>
                <p className="text-[15px] text-slate-300 leading-relaxed">
                  {getContainmentLimitations()}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* STRATEGIC EVOLUTION */}
        <section className="p-6 bg-white/5 border border-white/10 rounded-2xl">
          <SectionHeader
            title="Strategic Evolution & Lessons Learned"
            icon="fa-chart-line"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Incident Response</Label>
              <p className="text-[15px] text-slate-300 leading-relaxed mt-2">
                {getIncidentResponse()}
              </p>
            </div>
            <div>
              <Label>Modernized Defense Strategy</Label>
              <p className="text-[15px] text-slate-300 leading-relaxed mt-2">
                {getModernizedDefense()}
              </p>
            </div>
          </div>
        </section>

        {/* ATTACK SEQUENCE */}
        <section className="p-6 bg-white/5 border border-white/10 rounded-2xl">
          <SectionHeader title="Attack Sequence" icon="fa-list-ol" />
          <div className="space-y-3">
            {renderOptionalArray(
              event.attackSequence,
              "Attack sequence timeline not available"
            )}
            {event.attackSequence && event.attackSequence.length > 0 && (
              <ol className="space-y-2">
                {event.attackSequence.map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 text-[12px] font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="text-[14px] text-slate-300 leading-relaxed">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>

        {/* MAIN STORY SECTION */}
        <section className="pt-10 space-y-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              The Chronicle
            </h2>
            <div className="flex items-center justify-center gap-3 text-blue-400 text-[14px]">
              <i className="fa-solid fa-scroll"></i>
              <span className="font-medium">Deep Narrative Archive</span>
              <i className="fa-solid fa-scroll"></i>
            </div>
          </div>

          {/* STORY CONTAINER */}
          <div className="bg-gradient-to-br from-slate-900/50 to-slate-950/70 p-6 md:p-10 rounded-2xl border border-slate-700/30 shadow-xl">
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute -left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/20 via-blue-400/10 to-transparent"></div>

                {/* STORY CONTENT */}
                <div className="text-[17px] leading-[1.8] text-slate-300 font-serif space-y-6 pl-2">
                  {event.persuasiveStory.split("\n\n").map((para, i) => (
                    <div key={i} className="relative">
                      <p className="first-letter:font-bold first-letter:text-blue-400">
                        {para}
                      </p>
                      {i < event.persuasiveStory.split("\n\n").length - 1 && (
                        <div className="pt-4 border-t border-slate-700/20 mt-6"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* STORY METADATA */}
              <div className="mt-10 pt-6 border-t border-slate-700/30">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-slate-500 text-[13px]">
                    <i className="fa-solid fa-clock"></i>
                    <span>
                      Reading time:{" "}
                      {Math.ceil(event.persuasiveStory.length / 1000)} min
                    </span>
                  </div>

                  {hasReferences && (
                    <div>
                      <p className="text-[13px] font-medium text-slate-400 mb-2">
                        Intelligence Sources
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {event.references?.slice(0, 3).map((ref, i) => (
                          <span
                            key={i}
                            className="text-[12px] text-slate-500 bg-white/5 px-3 py-1 rounded border border-white/10"
                          >
                            [
                            {ref.length > 30
                              ? ref.substring(0, 30) + "..."
                              : ref}
                            ]
                          </span>
                        ))}
                        {event.references && event.references.length > 3 && (
                          <span className="text-[12px] text-slate-500">
                            +{event.references.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AFFILIATE SECTION (Only if data exists) */}
        {hasAffiliate && event.affiliateInfo && (
          <section className="pt-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-1 rounded-2xl">
              <div className="bg-slate-950 p-6 md:p-10 rounded-[1.3rem] flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="space-y-4 max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full">
                    <i className="fa-solid fa-shield-halved text-[10px] text-blue-400"></i>
                    <span className="text-[10px] font-medium uppercase tracking-wider text-blue-400">
                      Defense Briefing
                    </span>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-2xl font-bold text-white">
                      Secure your node: {event.affiliateInfo.productName}
                    </h4>
                    <p className="text-[15px] text-slate-300 leading-relaxed">
                      {event.affiliateInfo.description}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <span className="text-xl font-bold text-blue-400">
                      {event.affiliateInfo.priceTag || "DEPLOY READY"}
                    </span>
                  </div>
                </div>
                <a
                  href={event.affiliateInfo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-white text-slate-950 font-bold uppercase tracking-wider text-[12px] rounded-lg hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2"
                >
                  <span>
                    {event.affiliateInfo.ctaText || "Initialize Deployment"}
                  </span>
                  <i className="fa-solid fa-chevron-right text-[10px]"></i>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* FOOTER NAVIGATION */}
        <div className="pt-12 flex flex-col items-center gap-6">
          <Link
            to={`/year/${event.eventYear}`}
            className="px-8 py-3 border border-white/10 hover:border-blue-500 rounded-xl text-[12px] font-medium text-slate-400 hover:text-white transition-all flex items-center gap-2"
          >
            <i className="fa-solid fa-arrow-left"></i>
            Back to {displayYear} Records
          </Link>
          <div className="h-px w-16 bg-white/10"></div>
          <p className="text-[10px] text-slate-600">
            Archive • Node {event.id} • Last Updated:{" "}
            {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </>
  );
};

export default EventDetailPage;
